import { TIER_1_GENRE_ANCHORS } from '../SunoReadiness/vocabularyData';

/**
 * What a Prompt Doctor rewrite dropped from the original style (AUTO-098): the BPM, the genres the style opens
 * with, and a few structure cues. Plain text checks, no model call, so the panel can say so on screen instead of
 * presenting a rewrite that lost them as a clean fix.
 */
export interface LostSignals {
  bpm: string | null;
  genres: string[];
  structure: string[];
  any: boolean;
}

/** Cues that say how the song moves. "drop" is left out on purpose: "Drop D" is a tuning. */
const STRUCTURE_CUES = ['half time', 'breakdown', 'cold stop', 'key change', 'false ending', 'tempo change'];

/** Lowercase, and let every run of punctuation, "_", "-" and spaces read as one space, so "half-time_breakdown," and "half time breakdown" match. */
function norm(text: string): string {
  return ` ${text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
}

function hasWord(normText: string, word: string): boolean {
  return normText.includes(` ${word} `);
}

function findBpm(text: string): string | null {
  const m = text.match(/(\d{2,3})\s*[_-]?\s*bpm/i) ?? text.match(/bpm\s*[:=]?\s*(\d{2,3})/i);
  return m ? m[1] : null;
}

/** The run of comma items at the start that name a genre (Studio and Roulette put the picked genres first). */
function leadingGenres(original: string): string[] {
  const out: string[] = [];
  for (const raw of original.split(',')) {
    const item = raw.trim();
    if (!item || item.includes('_')) break; // a glued gear phrase, not a genre name
    const n = norm(item);
    if (!TIER_1_GENRE_ANCHORS.some(a => n.includes(norm(a)))) break;
    out.push(item);
    if (out.length >= 6) break;
  }
  return out;
}

/**
 * `conflicts` are the genre pairs the readiness check flagged as clashing. Dropping one of a flagged pair is
 * what the Doctor is told to do, so it is not reported as lost while the other half is still in the rewrite.
 */
export function findLostSignals(original: string, rewrite: string, conflicts: [string, string][] = []): LostSignals {
  const o = norm(original);
  const r = norm(rewrite);

  const bpmValue = findBpm(original);
  const bpm = bpmValue && !new RegExp(`(?<!\\d)${bpmValue}(?!\\d)`).test(rewrite) ? bpmValue : null;

  const droppedOnPurpose = (item: string): boolean => {
    const n = norm(item);
    return conflicts.some(([a, b]) => {
      const other = n.includes(norm(a)) ? b : n.includes(norm(b)) ? a : null;
      return other !== null && r.includes(norm(other));
    });
  };
  const genres = leadingGenres(original).filter(
    g => !norm(g).trim().split(' ').every(w => hasWord(r, w)) && !droppedOnPurpose(g),
  );

  const structure = STRUCTURE_CUES.filter(c => hasWord(o, c) && !hasWord(r, c));

  return { bpm, genres, structure, any: bpm !== null || genres.length > 0 || structure.length > 0 };
}
